import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_debounced_function_async } from '@/hooks/debounce/use_debounced_function_async';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { use_slice_user } from '@/hooks/store/use_slices';
import { local_user_settings_upsert } from '@/state/user/settings/local_user_settings_upsert';
import { FN_USER_SETTINGS__update } from '@/utils/utils_client';
import { DTO__user_settings } from '@shared/dto/+user/user_settings/DTO__user_settings';
import { ENUM__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { timestamp_ms } from '@shared/utils/utils_time';
import { clamp } from 'es-toolkit';
import { useCallback, useRef } from 'react';

interface Props {
  div_grid: HTMLDivElement;
}

let { DEBOUNCE_GRID2D_CHANGE_CELL_SIZE_BY_SCROLL_REMOTE, THROTTLE_GRID2D_CHANGE_CELL_SIZE_BY_SCROLL_LOCAL } = CONSTANTS_TIME__client;

export function WATCHER_GRID2D__change_cell_size_by_scroll({ div_grid }: Props) {
  const dispatch = use_dispatch();

  const { user_settings } = use_slice_user(['user_settings']);

  const { on_user_uses_shortcut } = use_hint_shortcut(EShortcutType.GRID2D_CHANGE_CELL_SIZE_WITH_SCROLL);

  const last_time_updated_at_local = useRef(0);

  //we use useCallback here to avoid recreating debounced verion
  const update_grid2d_cell_size_remote_cached = useCallback(update_grid2d_cell_size_remote, []);

  const update_grid2d_cell_size_remote_debounced = use_debounced_function_async(
    update_grid2d_cell_size_remote_cached,
    DEBOUNCE_GRID2D_CHANGE_CELL_SIZE_BY_SCROLL_REMOTE,
  );

  /*
   * EFFECTS
   */
  use_effect(() => {
    div_grid.addEventListener('wheel', on_wheel, { passive: false });

    return () => div_grid.removeEventListener('wheel', on_wheel);
  }, [on_wheel, div_grid]);

  /*
   * EVENTS
   */

  function on_wheel(e: WheelEvent) {
    if (!e.altKey) return;

    if (e.deltaY == 0) return;

    e.preventDefault();

    let time_now = timestamp_ms();

    if (time_now - last_time_updated_at_local.current < THROTTLE_GRID2D_CHANGE_CELL_SIZE_BY_SCROLL_LOCAL) return; //to early call

    let index_addition = e.deltaY > 0 ? -1 : 1;

    let size_new = calculate_new_size(index_addition, user_settings.ui_grid2d_cell_size);

    if (user_settings.ui_grid2d_cell_size == size_new) return;

    on_user_uses_shortcut();

    last_time_updated_at_local.current = time_now;

    let settings_new = user_settings.clone({ ui_grid2d_cell_size: size_new });

    void update_grid2d_cell_size_remote_debounced(settings_new);

    dispatch(local_user_settings_upsert(settings_new));
  }

  /*
   * METHODS
   */

  async function update_grid2d_cell_size_remote(settings_new: DTO__user_settings) {
    await FN_USER_SETTINGS__update(settings_new.clone());
  }

  function calculate_new_size(index_addition: number, cell_size_current: ENUM__grid2d_cell_size) {
    let sizes = [ENUM__grid2d_cell_size.XS, ENUM__grid2d_cell_size.S, ENUM__grid2d_cell_size.M, ENUM__grid2d_cell_size.L, ENUM__grid2d_cell_size.XL];

    let index_current = sizes.indexOf(cell_size_current);

    let index_next = index_current + index_addition;

    index_next = clamp(index_next, 0, sizes.length - 1);

    return sizes[index_next];
  }

  return null;
}
